import { Seminar } from '@/pages/Seminar/types';
import {
  buildScheduleLabel,
  formatClock,
  formatFee,
  formatMonthDay,
  formatOf,
  isCareerSeminar,
  isGeneralOnline,
  isOpenSchedule,
  isSingleDay,
  remainingSeatsOf,
  shortRegion,
} from '@/components/domain/seminar/seminarDisplay';

/**
 * 상세 정보 표(모바일)·요약 카드(PC)에 들어갈 문구를 한 곳에서 만든다.
 * 서버 새 필드(scheduleLabel·format·sessions·remainingSeats)가 없으면 seminarDisplay 폴백을 따른다.
 */

export interface DetailInfoFlags {
  recruitmentNotStarted: boolean;
  recruitmentEnded: boolean;
  isFullyBooked: boolean;
  hasOnlineAccess: boolean;
  /** 본인이 접속 링크를 이미 열었는지(onlineAccess.accessedAt) — 동영상은 이제 환불 불가 */
  accessed?: boolean;
}

export interface SessionLine {
  label: string;
  title?: string;
}

export interface DetailInfoModel {
  isCareer: boolean;
  /** 모바일 정보 표 일정 — 당일이면 시각까지 */
  scheduleText: string;
  /** 회차형 회차 목록 */
  sessions: SessionLine[];
  /** 진행 방식(커리어) — 모바일 긴 문구 / PC 짧은 문구 */
  formatText: string | null;
  formatShort: string | null;
  /** 장소 전체 주소(오프라인) 또는 '온라인'(일반 온라인) */
  placeText: string | null;
  /** PC 요약의 짧은 장소(시·구) */
  placeShort: string | null;
  feeText: string;
  fee: number;
  /** 모집(커리어) */
  recruitText: string | null;
  recruitShort: string | null;
  /** 환불(커리어 유료) — 환불 기준일(시작일/시청 시작일) 기준 한 줄 */
  refundText: string | null;
  refundShort: string | null;
  /** 환불 행 아래 한 줄(동영상) — "접속 링크를 열면 환불할 수 없어요." */
  refundNote: string | null;
  /** 서비스 제공 기간(커리어 유료) — PG 심사 요건: 무형 상품은 상품 페이지에서 제공 기간이 보여야 한다 */
  provideText: string | null;
}

const addressText = (seminar: Seminar): string => {
  const location = seminar.location;
  if (typeof location === 'string') return location;
  return location?.fullAddress || location?.address || seminar.address || '';
};

const scheduleWithTime = (seminar: Seminar): string => {
  const label: string = buildScheduleLabel(seminar);
  // 당일만 시각을 붙인다(기간·회차는 회차 목록/기간이 곧 일정)
  const single: boolean = !seminar.schedule || seminar.schedule === 'SINGLE';
  if (!seminar.startDateTime || !single || !isSingleDay(seminar)) return label;
  const start: string = formatClock(seminar.startDateTime);
  const end: string = formatClock(seminar.endDateTime);
  return end ? `${label} ${start} ~ ${end}` : `${label} ${start}`;
};

export const buildDetailInfo = (seminar: Seminar, flags: DetailInfoFlags): DetailInfoModel => {
  const isCareer: boolean = isCareerSeminar(seminar);
  const format = formatOf(seminar);
  const fee: number = seminar.participationFee ?? seminar.price ?? 0;
  const address: string = addressText(seminar);

  const sessions: SessionLine[] = isCareer && seminar.schedule === 'SESSIONS'
    ? (seminar.sessions ?? []).map((session, index: number) => ({
        label: `${index + 1}회 · ${formatMonthDay(session.startDateTime)} ${formatClock(session.startDateTime)}~${formatClock(session.endDateTime)}`,
        title: session.title,
      }))
    : [];

  let formatText: string | null = null;
  let formatShort: string | null = null;
  if (isCareer) {
    if (format === 'LIVE') {
      formatText = flags.hasOnlineAccess ? '라이브 · 아래 참가 정보에서 입장' : '라이브 · 접속 링크는 신청 확정 후 안내';
      formatShort = flags.hasOnlineAccess ? '라이브' : '라이브 · 링크는 신청 확정 후';
    } else if (format === 'VOD') {
      formatText = flags.hasOnlineAccess ? '동영상 · 아래 참가 정보에서 시청' : '동영상 · 시청 링크는 신청 확정 후 안내';
      formatShort = flags.hasOnlineAccess ? '동영상' : '동영상 · 링크는 신청 확정 후';
    } else {
      formatText = '오프라인';
      const region: string = shortRegion(address);
      formatShort = region ? `오프라인 · ${region}` : '오프라인';
    }
  }

  const online: boolean = isGeneralOnline(seminar) || (isCareer && format !== 'OFFLINE');
  const placeText: string | null = isGeneralOnline(seminar) ? '온라인' : online ? null : address || '장소 미정';
  const placeShort: string | null = isGeneralOnline(seminar) ? '온라인' : online ? null : shortRegion(address) || null;

  let recruitText: string | null = null;
  let recruitShort: string | null = null;
  if (isCareer) {
    const remaining: number | null = remainingSeatsOf(seminar);
    if (seminar.salesClosed) {
      recruitText = '판매 종료';
      recruitShort = '판매 종료';
    } else if (flags.recruitmentNotStarted && seminar.recruitmentStartDateTime) {
      recruitText = `${formatMonthDay(seminar.recruitmentStartDateTime)} ${formatClock(seminar.recruitmentStartDateTime)}부터`;
      recruitShort = `${formatMonthDay(seminar.recruitmentStartDateTime)}부터`;
    } else if (flags.recruitmentEnded) {
      recruitText = '모집 마감';
      recruitShort = '모집 마감';
    } else if (flags.isFullyBooked) {
      recruitText = '정원 마감';
      recruitShort = '정원 마감';
    } else if (seminar.recruitmentEndDateTime) {
      const seats: string = remaining !== null ? ` · 남은 자리 ${remaining}` : '';
      recruitText = `${formatMonthDay(seminar.recruitmentEndDateTime)} ${formatClock(seminar.recruitmentEndDateTime)}까지${seats}`;
      recruitShort = `${formatMonthDay(seminar.recruitmentEndDateTime)}까지${seats}`;
    } else if (remaining !== null) {
      recruitText = `남은 자리 ${remaining}`;
      recruitShort = recruitText;
    }
  }

  const paidCareer: boolean = isCareer && fee > 0;
  // 상시 동영상(OPEN)은 시작일 기준이 없다 — 결제 후 7일 이내·접속 전 전액. endDateTime 은 자리값이라 쓰지 않는다
  const open: boolean = isOpenSchedule(seminar);
  const basis: string = format === 'VOD' ? '시청 시작' : '시작';
  const refundText: string | null = !paidCareer
    ? null
    : open ? '결제 후 7일 이내 · 접속 전 100% · 이후 불가' : `${basis} 7일 전 100% · 3일 전 50% · 이후 불가`;
  const refundShort: string | null = !paidCareer
    ? null
    : open ? '결제 후 7일 · 접속 전 100%' : `${basis} 7일 전 100%`;
  // 동영상 — 아직 안 열었으면 규칙, 이미 열었으면 지금 상태 한 줄
  const refundNote: string | null = !paidCareer || format !== 'VOD'
    ? null
    : flags.accessed ? '접속 링크를 열어 환불할 수 없어요.' : '접속 링크를 열면 환불할 수 없어요.';
  const provideEnd: string | undefined = seminar.endDateTime || seminar.startDateTime;
  const provideText: string | null = !paidCareer
    ? null
    : open ? '결제 완료부터 계속(판매가 끝나도 볼 수 있어요)'
      : provideEnd ? `결제 완료 ~ ${formatMonthDay(provideEnd)} ${formatClock(provideEnd)}` : null;

  return {
    isCareer,
    scheduleText: scheduleWithTime(seminar),
    sessions,
    formatText,
    formatShort,
    placeText,
    placeShort,
    feeText: formatFee(fee),
    fee,
    recruitText,
    recruitShort,
    refundText,
    refundShort,
    refundNote,
    provideText,
  };
};
